import React, { useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { SupportedLanguages } from '@atlaskit/code/constants';
import CodeBlock from '@atlaskit/code/code-block';
import Heading from '@atlaskit/heading/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import ErrorBoundary from '../error-boundary';
import { useUserPreferences } from '../user-preferences';

import { ExampleActions } from './actions';
import type { CodeSandboxProps } from './actions/code-sandbox';
import { transformCode } from './code-transforms';
import CollapseButton from './collapse-button';
import ExampleCode from './example-code';
import { ExampleFeatureFlagProvider } from './example-feature-flag-context';
import ExampleShowcase from './example-showcase';
import { resolveFeatureFlagSwitchers } from './feature-flag-switcher-configs';
import type {
	Background,
	ColorMode,
	FeatureFlagDefaults,
	FeatureFlagSwitcherIds,
	SectionIds,
} from './types';

const EXAMPLE_HINT_HEIGHT = 200;
const LINE_TO_HEIGHT_FACTOR = 1.66667 * 12;

type CodeTransformIds = [];

export interface ExampleProps {
	/**
	 * The component to render in the example
	 */
	Component: any;

	/**
	 * The source code for the component; if none is provided, will be generated
	 * from the `Component` rendered.
	 */
	source?: string;

	/**
	 * The language in which the code is written
	 */
	language?: string;

	/**
	 * The name of the package demonstrated in the example. Used to replace relative references
	 * in the example code
	 */
	packageName?: string;

	/**
	 * Comma and hyphen-separated list of lines to highlight in the source view
	 */
	highlight?: string;

	/**
	 * Height of the collapsed source view, in pixels
	 */
	hintHeight?: number;

	/**
	 * Controls the background color of the example showcase
	 */
	backgroundColor?: Background;

	/**
	 * Used for a11y labelling
	 */
	exampleName?: string;

	/**
	 * Manually provides the required information for CodeSandbox integration.
	 *
	 * If this is not provided and there are no meta attributes on the passed in `Component`
	 * then CodeSandbox integration will be disabled.
	 */
	sandboxInfo?: CodeSandboxProps;
	/**
	 * Will disable the CodeSandbox button appearing. Useful for examples
	 * that are incomplete, only demonstrating partial implementations
	 * and cannot be run in CodeSandbox.
	 */
	isCodeSandboxDisabled?: boolean;
	/**
	 * Controls what content is visible.
	 */
	appearance?: 'showcase-and-source' | 'showcase-only' | 'source-only';
	testId?: string;
	/**
	 * A list of code transformations to disable for individual examples.
	 * This is useful for examples that are not compatible with the default code transformations.
	 *
	 * For example, if you want to disable the migration icons code transformation for an example
	 * that demonstrates migration icons.
	 */
	disableCodeTransforms?: CodeTransformIds;
	hiddenSections?: SectionIds;
	/**
	 * Flags to apply for this example with no UI (e.g. enable labelling visuals). Prefer this over
	 * path-based page config so each example declares what it needs.
	 */
	featureFlagDefaults?: FeatureFlagDefaults;
	/**
	 * Feature-flag switcher IDs to show in the Styles popover (e.g. ['labelling'] for New labelling).
	 * Matched to internal presets; user choice overrides featureFlagDefaults for that flag.
	 */
	featureFlagSwitchers?: FeatureFlagSwitcherIds;
}

interface ExamplePropsWithComponents extends ExampleProps {
	components?: any;
}

/**
 * __Example__
 *
 * An example renders code, along with the code as text.
 *
 */
export const Example = (props: ExamplePropsWithComponents): React.JSX.Element => {
	const {
		Component,
		language = 'tsx',
		highlight = '',
		hintHeight = EXAMPLE_HINT_HEIGHT,
		backgroundColor = 'checkered',
		components,
		appearance = 'showcase-and-source',
		testId,
		disableCodeTransforms,
		hiddenSections,
		isCodeSandboxDisabled,
		featureFlagDefaults,
		featureFlagSwitchers,
	} = props;

	const packageName = props.packageName ?? Component?.__pkgJSON?.name;
	const effectiveFeatureFlagSwitchers = resolveFeatureFlagSwitchers(
		featureFlagSwitchers ?? [],
		packageName,
	);
	const [collapsed, setCollapsed] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireButtonAnalytics = () => {
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'button',
			action: 'clicked',
		});
		const actionSubjectId = collapsed ? 'showMoreExample' : 'hideMoreExample';

		fireUIAnalytics(analyticsEvent, actionSubjectId, {
			exampleName,
			packageName,
		});
	};

	const isShowcaseVisible = appearance !== 'source-only';
	const isSourceCodeVisible = appearance !== 'showcase-only';

	const { userPreferences } = useUserPreferences();

	const [colorMode, setColorMode] = useState<ColorMode>(
		userPreferences['exampleColorMode'] || userPreferences['colorMode'] || 'auto',
	);

	const source = props.source ?? Component?.__raw ?? '';
	const transformedSource = transformCode(source, packageName, {
		disableCodeTransforms,
	});

	const showCollapseUI =
		transformedSource.split(/\r\n|\r|\n/).length > hintHeight / LINE_TO_HEIGHT_FACTOR;

	const exampleName = props.exampleName ?? Component?.__path?.replace?.(/^([^/]*\/)+/, '') ?? '';
	const exampleNameSpoken = exampleName.replace(/\-/g, ' ');

	const sandboxInfo: CodeSandboxProps | undefined =
		props.sandboxInfo ??
		inferSandboxInfo(Component, exampleName, components, {
			disableCodeTransforms,
		});

	const rootColorMode = userPreferences['colorMode'];
	const exampleColorMode = userPreferences['exampleColorMode'];
	const exampleSyncSettings = userPreferences['exampleSyncSettings'];

	/**
	 * Syncs example color mode based on user preferences:
	 *
	 * When example settings ARE synced,
	 * use the example color mode preference.
	 * In this case the example color mode is independent of the root color mode,
	 * and will not be updated when the root color mode changes.
	 *
	 * When example settings ARE NOT synced,
	 * the example color mode can be controlled seperately elsewhere, but will also
	 * be updated here when the root color mode changes to create a more intuitive experience.
	 */
	useEffect(() => {
		if (exampleSyncSettings === 'true' && exampleColorMode) {
			setColorMode(exampleColorMode);
		} else {
			setColorMode(rootColorMode || 'auto');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exampleColorMode, rootColorMode, exampleSyncSettings]);

	const exampleContent = (
		<>
			{isShowcaseVisible && (
				<ExampleShowcase
					backgroundColor={backgroundColor}
					aria-label={`${exampleNameSpoken} example showcase`}
					colorMode={colorMode}
					testId={testId ? `${testId}--showcase` : undefined}
				>
					<ErrorBoundary
						fallback={<Heading size="small">Something went wrong loading this example.</Heading>}
					>
						<Component />
					</ErrorBoundary>
				</ExampleShowcase>
			)}

			<ExampleActions
				codeSandboxInfo={sandboxInfo}
				isCodeSandboxDisabled={isCodeSandboxDisabled}
				isShowcaseVisible={isShowcaseVisible}
				isSourceCodeVisible={isSourceCodeVisible}
				sourceCode={transformedSource}
				appearanceControls={{
					colorMode,
					onColorModeChange: setColorMode,
					hiddenSections,
					featureFlagSwitchers: effectiveFeatureFlagSwitchers,
				}}
				testId={testId ? `${testId}--actions` : undefined}
			/>

			{isSourceCodeVisible && (
				<>
					<ExampleCode
						isCollapsed={showCollapseUI ? collapsed : false}
						hintHeight={hintHeight}
						onMouseDown={() => setCollapsed(false)}
						aria-label={`${exampleNameSpoken} code example`}
						role="group"
					>
						<div id={`example-${exampleName}`}>
							<CodeBlock
								text={transformedSource}
								language={language as SupportedLanguages}
								showLineNumbers={false}
								highlight={highlight}
								shouldWrapLongLines={true}
							/>
						</div>
					</ExampleCode>
					{showCollapseUI && (
						<CollapseButton
							collapsed={collapsed}
							onClick={() => {
								setCollapsed(!collapsed);
								fireButtonAnalytics();
							}}
							aria-controls={`example-${exampleName}`}
						>
							{collapsed ? (
								<>
									{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
									<span aria-label={`Show more, ${exampleNameSpoken} code example`}>Show more</span>
									<ChevronDownIcon size="small" spacing="spacious" label="" />
								</>
							) : (
								<>
									{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
									<span aria-label={`Show less, ${exampleNameSpoken} code example`}>Show less</span>
									<ChevronUpIcon size="small" spacing="spacious" label="" />
								</>
							)}
						</CollapseButton>
					)}
				</>
			)}
		</>
	);

	const hasFeatureFlagConfig =
		(featureFlagDefaults && Object.keys(featureFlagDefaults).length > 0) ||
		effectiveFeatureFlagSwitchers.length > 0;

	return (
		<ExampleWrapper data-testid={testId}>
			{hasFeatureFlagConfig ? (
				<ExampleFeatureFlagProvider
					featureFlagDefaults={featureFlagDefaults}
					featureFlagSwitchers={effectiveFeatureFlagSwitchers}
				>
					{exampleContent}
				</ExampleFeatureFlagProvider>
			) : (
				exampleContent
			)}
		</ExampleWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExampleWrapper = styled.div({
	borderRadius: token('radius.small'),
	boxShadow: `0 0 0 1.2px ${token('color.border')}`,
	width: '100%',
});

// '../../x/y/z' -> 'x/y/z'
const removeRelativePathInfo = (s: string) => s.replace(/^(\.+\/)*/, '');

const inferSandboxInfo = (
	Component: any,
	exampleName: string,
	components: any,
	options?: {
		disableCodeTransforms?: CodeTransformIds;
	},
): CodeSandboxProps | undefined => {
	/**
	 * Check for the existence of properties added by `gatsby-theme-brisk/plugins/remark/add-raw-to-imports`
	 *
	 * If these aren't there then we cannot infer the sandbox info.
	 */
	if (!Component?.hasOwnProperty?.('__raw')) {
		return;
	}

	const pkgJSON = Component.__pkgJSON;

	const { groupId, packageId } = pkgJSON;
	const examplePath = `${groupId}/${packageId}/${removeRelativePathInfo(Component.__path)}.tsx`;

	const importReplacements: Array<[string, string]> = [
		[`${groupId}/${packageId}/src`, pkgJSON.name],
	];

	const transformedSource = transformCode(Component.__raw, pkgJSON.name, {
		disableCodeTransforms: options?.disableCodeTransforms,
	});

	return {
		example: transformedSource,
		examplePath,
		exampleName,
		importReplacements,
		pkgJSON: {
			...pkgJSON,
			devDependencies: {
				...Object.fromEntries(
					(components || []).map((component: any) => [component.name, component.version]),
				),
				...pkgJSON.devDependencies,
			},
		},
	};
};
